import React from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '../ui/Badge';
import { Meter } from '../ui/Meter';
import { ProfileCard, ProfileCardHeader, ProfileActionRow, ProfileField } from './ProfileCard';
import { PROFILE_EXPERIENCE, PROFILE_FIELDS } from '../../data/profileConfig';
import { useTerminology } from '../../hooks/useTerminology';
import { useAccount } from '../../contexts/AccountContext';
import { useAuth } from '../../contexts/AuthContext';
import { isLive } from '../platform/FeatureStatusBadge';
import type { CompletionOutcome } from '../../utils/profileCompletion';
import type { ProfileFieldConfig } from '../../types/profile';
import type { Identity } from '../../types/auth';
import type { Role, VisibilityLevel, VisibilitySurface } from '../../types/identity';

/** Profile completion, stated with the actual outstanding work beneath it. */
export function CompletionCard({
  outcome,
  onComplete
}: {
  outcome: CompletionOutcome;
  onComplete: () => void;
}) {
  return <ProfileCard className="p-5" data-id="element-4517" data-mp="3aqdd39z9lguw">
      <div className="flex items-baseline justify-between gap-3" data-id="element-4518" data-mp="3aroex495gejd">
        <h2 className="text-h4 font-semibold text-ink" data-id="element-4519" data-mp="35sbu0p1u9hjf">Profile completion</h2>
        <p className="cz-num text-h4 font-semibold text-ink" data-id="element-4520" data-mp="3kgpwuzz995zz">{outcome.percent}%</p>
      </div>
      <div className="mt-3" data-id="element-4521" data-mp="3fpzrvnuqr55t">
        <Meter value={outcome.percent} tone={outcome.percent >= 80 ? 'ok' : 'brand'} data-id="element-4522" data-mp="3fppz5sibajwf" />
      </div>
      <p className="mt-3 max-w-[46ch] text-body-sm text-ink-muted" data-id="element-4523" data-mp="3tgmqzh4eyx2f">
        {outcome.missing.length === 0 ? 'Everything on your profile is filled in. Nothing is outstanding.' : `Add your ${outcome.missing.slice(0, 2).map(field => field.label.toLowerCase()).join(' and ')} to get better opportunities, housing matches and recommendations.`}
      </p>
      {outcome.next && <button type="button" onClick={onComplete} className="mt-auto pt-4 text-left text-body-sm font-medium text-brand-700 hover:underline" data-id="element-4524" data-mp="291t4ph7ry0oe">
          Complete profile →
        </button>}
    </ProfileCard>;
}

/** About you — generated from the field register, never a hard-coded row list. */
export function AboutYouCard({
  identity,
  role,
  onEdit
}: {
  identity: Identity;
  role: Role;
  onEdit: (field: ProfileFieldConfig) => void;
}) {
  const fields = PROFILE_FIELDS.filter(field => field.roles.length === 0 || field.roles.includes(role));
  return <ProfileCard data-id="element-4525" data-mp="qwcxr8atkpiw">
      <ProfileCardHeader title="About you" description="Shown to others according to your visibility settings." data-id="element-4526" data-mp="vx0qpzilzgfr" />
      <ul className="mt-4 divide-y divide-line" data-id="element-4527" data-mp="10pa9mvlb3g8j">
        {fields.map(field => <ProfileField key={field.id} icon={field.icon} label={field.label} value={displayValue(identity, field)} emptyLabel={field.emptyLabel} onEdit={field.editable ? () => onEdit(field) : undefined} data-id="element-4528" data-mp="14oj462o67i55" />)}
      </ul>
    </ProfileCard>;
}
function displayValue(identity: Identity, field: ProfileFieldConfig): string | null {
  const list = (values: string[] | undefined) => values && values.length > 0 ? values.join(', ') : null;
  switch (field.id) {
    case 'bio':
      return identity.bio?.trim() || null;
    case 'location':
      return identity.location?.trim() || null;
    case 'interests':
      return list(identity.interests);
    case 'skills':
      return list(identity.skills);
    case 'lookingFor':
      return list(identity.lookingFor);
    case 'languages':
      return list(identity.languages);
    default:
      return null;
  }
}

/** Your Campuzon experience — configured destinations, resolved terminology. */
export function ExperienceCard({
  role
}: {
  role: Role;
}) {
  const {
    phrase
  } = useTerminology();
  const rows = PROFILE_EXPERIENCE.filter(row => row.roles.length === 0 || row.roles.includes(role));
  return <ProfileCard data-id="element-4529" data-mp="2lfv4z7atl41d">
      <ProfileCardHeader title={phrase('Your {platform} experience')} description="Each of these opens the surface that owns the setting, so a preference lives in one place." data-id="element-4530" data-mp="25zy6ujkmtfhd" />
      <ul className="mt-4 divide-y divide-line" data-id="element-4531" data-mp="1wz9s1mnezb3f">
        {rows.map(row => <li key={row.id} data-id="element-4532" data-mp="1x1mfugfusfoe">
            <ProfileActionRow icon={row.icon} label={row.label} description={row.description} to={row.to} data-id="element-4533" data-mp="210bppz1onf3a" />
          </li>)}
      </ul>
    </ProfileCard>;
}
const VISIBILITY_LABEL: Record<VisibilityLevel, string> = {
  private: 'Only me',
  connections: 'People I accept',
  campus: 'People at my institution',
  public: 'Anyone on Campuzon'
};
const SURFACES: {
  key: VisibilitySurface;
  label: string;
}[] = [{
  key: 'academic',
  label: 'Academic information'
}, {
  key: 'housing',
  label: '{housing} activity'
}, {
  key: 'roommate',
  label: '{roommate} profile'
}, {
  key: 'career',
  label: 'Career profile'
}, {
  key: 'contact',
  label: 'Contact information'
}];

/**
 * Privacy, stated as who can see it rather than as a setting name. The controls
 * themselves stay in Settings, which owns them — this reports the current answer
 * and links there, so there is one place a visibility level is changed.
 */
export function PrivacyCard() {
  const {
    account,
    signals
  } = useAccount();
  const activeSignals = Object.values(signals).filter(Boolean).length;
  return <ProfileCard data-id="element-4534" data-mp="i2p5c1jiuvfm">
      <ProfileCardHeader icon="privacy" title="Privacy & visibility" description="Manage who can see your information." tone="neutral" data-id="element-4535" data-mp="i49u1aj5nk5k" />
      <ul className="mt-3.5 divide-y divide-line" data-id="element-4536" data-mp="s1xxcouttzzc">
        {SURFACES.map(surface => <li key={surface.key} data-id="element-4537" data-mp="n0h1j7r793pm">
            <ProfileActionRow icon="visibility" label={surface.label} to="/settings" trailing={<span className="shrink-0 text-body-sm font-medium text-brand-700" data-id="element-4539" data-mp="1yfdtaperq70o">
                  {VISIBILITY_LABEL[account.visibility[surface.key]]}
                </span>} data-id="element-4538" data-mp="n3mq8lvx23kt" />
          </li>)}
        <li data-id="element-4540" data-mp="2agvljow82xr9">
          <ProfileActionRow icon="preferences" label="Personalised recommendations" to="/settings" trailing={<span className="shrink-0 text-body-sm font-medium text-brand-700" data-id="element-4542" data-mp="2kfl47jvy0m8k">
                {activeSignals === 0 ? 'Off' : `${activeSignals} signals`}
              </span>} data-id="element-4541" data-mp="2akjd4ii3xa3c" />
        </li>
      </ul>
      <p className="border-t border-line px-5 py-3 text-body-sm text-ink-muted" data-id="element-4543" data-mp="1rsi9z3yauxo4">
        Your notes, diary, wellbeing records and private messages are never shown to anyone,
        whatever these are set to.
      </p>
    </ProfileCard>;
}

/**
 * Security, read from the real identity and session records. Nothing here is
 * asserted: where a capability is not enabled, it says so rather than claiming a
 * protection the account does not have.
 */
export function SecurityCard() {
  const {
    identity,
    sessions
  } = useAuth();
  if (!identity) return null;
  const changed = identity.passwordUpdatedAt ? relative(identity.passwordUpdatedAt) : 'Never changed';
  const active = sessions.filter(session => !session.expiresAt || new Date(session.expiresAt) > new Date());
  // Read from the platform feature register rather than asserted here, so the
  // profile cannot claim a protection the deployment has not actually enabled.
  const mfaAvailable = isLive('mfa_totp');
  return <ProfileCard data-id="element-4544" data-mp="2yl4wsqw6bj9d">
      <ProfileCardHeader icon="security" title="Security" description="Keep your account safe and secure." tone="ok" data-id="element-4545" data-mp="2ykldp6useotr" />
      <ul className="mt-3.5 divide-y divide-line" data-id="element-4546" data-mp="2pmt0c88mlr90">
        <li data-id="element-4547" data-mp="2kmncz49e2qwq">
          <ProfileActionRow icon="password" label="Password" to="/settings" trailing={<span className="shrink-0 text-body-sm text-ink-muted" data-id="element-4549" data-mp="2fov879nnbab4">Last changed {changed}</span>} data-id="element-4548" data-mp="2kmdl1b4sa9oe" />
        </li>
        <li data-id="element-4550" data-mp="8a97t895ic1p">
          <ProfileActionRow icon="security" label="Two-factor authentication" to="/settings" trailing={mfaAvailable ? <Badge tone="ok" data-id="element-4552" data-mp="3ulxwoz099g7w">Enabled</Badge> : <Badge tone="neutral" data-id="element-4553" data-mp="3bpg0r7vm61f">Not yet available</Badge>} data-id="element-4551" data-mp="cdyozcnhxrcp" />
        </li>
        <li data-id="element-4554" data-mp="3ksh581nlcd7e">
          <ProfileActionRow icon="sessions" label="Active sessions" to="/settings" trailing={<span className="cz-num shrink-0 text-body-sm text-ink-muted" data-id="element-4556" data-mp="3btvf9lfshvwl">
                {active.length} active
              </span>} data-id="element-4555" data-mp="3krxkyeagxcq7" />
        </li>
      </ul>
      <Link to="/settings" className="border-t border-line px-5 py-3 text-body-sm font-medium text-brand-700 hover:underline" data-id="element-4557" data-mp="31v6225yu8zmo">
        Security settings →
      </Link>
    </ProfileCard>;
}
function relative(iso: string): string {
  const then = new Date(iso).getTime();
  const days = Math.max(0, Math.round((Date.now() - then) / 86_400_000));
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 31) return `${days} days ago`;
  const months = Math.round(days / 30);
  return `${months} month${months === 1 ? '' : 's'} ago`;
}

/** Data and account. The destructive actions are handled by the page's dialog. */
export function DataAccountCard({
  onExport,
  onDeactivate,
  onDelete
}: {
  onExport: () => void;
  onDeactivate: () => void;
  onDelete: () => void;
}) {
  return <ProfileCard data-id="element-4558" data-mp="3fvjrq5ndhktv">
      <ProfileCardHeader icon="data" title="Your data & account" description="Download or manage your account and data." tone="neutral" data-id="element-4559" data-mp="3fw3atporef9h" />
      <ul className="mt-3.5 divide-y divide-line" data-id="element-4560" data-mp="3pw3ujcqjixe7">
        <li data-id="element-4561" data-mp="3kvy1pt8jb4uj">
          <ProfileActionRow icon="download" label="Download my data" description="Export your personal data" onClick={onExport} data-id="element-4562" data-mp="3kw7tnmd53m2v" />
        </li>
        <li data-id="element-4563" data-mp="xhr9tvlkc19q">
          <ProfileActionRow icon="privacy" label="Manage data preferences" description="Control your data and privacy" to="/settings" data-id="element-4564" data-mp="xh950aku4eq7" />
        </li>
        <li data-id="element-4565" data-mp="ohm5jubo1izs">
          <ProfileActionRow icon="sessions" label="Deactivate account" description="Temporarily deactivate your account" onClick={onDeactivate} data-id="element-4566" data-mp="oi46h499wle1" />
        </li>
        <li data-id="element-4567" data-mp="1g8s78f71bor6">
          <ProfileActionRow icon="delete" label="Delete account" description="Permanently delete your account" onClick={onDelete} danger data-id="element-4568" data-mp="1g5w6yk7psnzk" />
        </li>
      </ul>
    </ProfileCard>;
}